import axios, { type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore, clearUserScopedStorage } from '../stores/authStore'
import { queryClient } from '../lib/queryClient'
import type { TokenResponse, User } from '../types'

// ── Axios instance ─────────────────────────────────────────────────────────────

const api = axios.create({
  // Use relative URLs so requests always go to the same origin as the page.
  // In local dev the Vite proxy forwards /api/ to the backend; in production
  // nginx handles the routing. This avoids cross-origin issues regardless of
  // which port the app is accessed from.
  baseURL: '',
  headers: { 'Content-Type': 'application/json' },
  // A request that never settles is not merely slow — it holds a card's
  // save-visibility window open with no upper bound at all, and the editing
  // surface defers its next write until the one in flight answers
  // (docs/diagnostics/deck-save-visibility-window-2026-08-11.md §8). A timeout
  // is what turns "indefinitely" into a bound; the failure is then reported
  // through the same save-error path every other failure takes.
  //
  // Sixty seconds, not a tight number: the slowest legitimate calls on this
  // client are AI-backed (tutor explanations, batch card generation, PDF
  // import), and a bound tighter than those would fail real work in order to
  // shorten a window measured in single round trips.
  //
  // Deliberately NO retry. A retried write is a second full-replace PATCH built
  // from the same payload, which is the opposite of what this change is for.
  timeout: 60_000,
})

// ── Request interceptor — attach access token ──────────────────────────────────

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ── Response interceptor — refresh on 401 ─────────────────────────────────────

let isRefreshing = false
let failedQueue: Array<{ resolve: (v: string) => void; reject: (e: unknown) => void }> = []

function redirectToLogin() {
  if (window.location.pathname !== '/login') {
    window.location.replace('/login')
  }
}

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token!)
  })
  failedQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Non-401s never touch the session. A 403 in particular is passed straight
    // through to the caller: it carries domain rejections (a wrong current
    // password, a Pro-gated action), never session expiry. This is why the
    // backend answers a mistyped current password with 403, not 401 — a 401 from
    // an auth route below is treated as a dead session and signs the user out.
    // PRO_REQUIRED modal is intentionally disabled pending monetization design.
    // Re-enable by restoring: useProModalStore.getState().open() on code === 'PRO_REQUIRED'.
    if (error.response?.status === 403) {
      return Promise.reject(error)
    }

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error)
    }

    // A 401 from an auth route means the session itself is unusable (a dead/absent
    // token — get_current_user rejected it). Refresh cannot recover it, so sign
    // out. Domain rejections must NOT reach here as 401 (see the 403 note above);
    // non-auth routes below still refresh transparently on a 401.
    if (original.url?.includes('/api/v1/auth/')) {
      useAuthStore.getState().logout()
      redirectToLogin()
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`
        return api(original)
      })
    }

    original._retry = true
    isRefreshing = true

    const { refreshToken, logout, setTokens } = useAuthStore.getState()
    if (!refreshToken) {
      logout()
      isRefreshing = false
      redirectToLogin()
      return Promise.reject(error)
    }

    try {
      // refresh endpoint takes the token in the request body
      const { data } = await axios.post<TokenResponse>(
        `${api.defaults.baseURL}/api/v1/auth/refresh`,
        { refresh_token: refreshToken },
      )
      setTokens(data.access_token, data.refresh_token)
      processQueue(null, data.access_token)
      original.headers.Authorization = `Bearer ${data.access_token}`
      return api(original)
    } catch (refreshError) {
      processQueue(refreshError, null)
      logout()
      redirectToLogin()
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  },
)

// ── Auth helpers ───────────────────────────────────────────────────────────────

export async function login(email: string, password: string): Promise<void> {
  const { data: tokens } = await api.post<TokenResponse>('/api/v1/auth/login', {
    email,
    password,
    // ADR-081 Decision 3: zero-prompt, zero-permission — same call the push
    // subscribe flow already makes. Overwritten on every sign-in.
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })
  // Fetch user with fresh access token
  const { data: user } = await axios.get<User>(
    `${api.defaults.baseURL}/api/v1/auth/me`,
    { headers: { Authorization: `Bearer ${tokens.access_token}` } },
  )
  // Purge any prior user's cached data before writing the new identity.
  // Covers the login-over-existing-session path where logout() is never called.
  clearUserScopedStorage()
  queryClient.clear()
  useAuthStore.getState().login(user, tokens.access_token, tokens.refresh_token)
}

export async function register(
  email: string,
  password: string,
  display_name?: string,
  interface_language?: string,
): Promise<void> {
  const { data: tokens } = await api.post<TokenResponse>('/api/v1/auth/register', {
    email,
    password,
    display_name,
    // The language the visitor is looking at, so the account starts in it.
    interface_language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })
  const { data: user } = await axios.get<User>(
    `${api.defaults.baseURL}/api/v1/auth/me`,
    { headers: { Authorization: `Bearer ${tokens.access_token}` } },
  )
  clearUserScopedStorage()
  queryClient.clear()
  useAuthStore.getState().login(user, tokens.access_token, tokens.refresh_token)
}

/**
 * Change the signed-in user's password (ADR-061 §1). The backend returns a
 * fresh token pair for this device (it revokes every *other* refresh token via
 * a token_epoch bump), so we must swap the stored tokens in — otherwise the now-
 * revoked refresh token would log this device out on its next refresh.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const { data } = await api.post<TokenResponse>('/api/v1/auth/password', {
    current_password: currentPassword,
    new_password: newPassword,
  })
  useAuthStore.getState().setTokens(data.access_token, data.refresh_token)
}

/**
 * Redeem a one-time password-reset token (ADR-061 §4). Public — no session and
 * no auth header needed; the caller is a locked-out user. The backend returns
 * NO token pair (reset ends every session, including any the requester held), so
 * we deliberately do not touch the auth store here — the user signs in normally
 * afterward. Uses a bare axios call so the response interceptor's 401→refresh
 * machinery never engages for this session-less flow.
 */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await axios.post(`${api.defaults.baseURL}/api/v1/auth/password/reset`, {
    token,
    new_password: newPassword,
  })
}

export async function logoutApi(): Promise<void> {
  const { refreshToken, logout } = useAuthStore.getState()
  if (refreshToken) {
    await api.post('/api/v1/auth/logout', { refresh_token: refreshToken }).catch(() => {})
  }
  logout()
}

// ── Error helpers ──────────────────────────────────────────────────────────────

/**
 * Extracts a human-readable string from an axios error.
 *
 * Trusts exactly one shape: the CardSmith envelope every backend error
 * response carries, `{"detail": {"detail": "string", "code": "...", "errors":
 * [...]}}"` (`backend/app/core/exception_handlers.py`, ADR-034) — and only
 * once the inner `code` sibling confirms the body actually came from our own
 * exception handlers. Anything ahead of the app in the request path — a
 * reverse proxy, a gateway, a load balancer — can answer with its own JSON
 * body shaped just enough like ours to slip through a looser check (a flat
 * `{"detail": "..."}` with no `code`, e.g. a gateway's "the origin server
 * returned an invalid response"). That text is a diagnostic for an operator,
 * not copy for a reader, and must fall through to `fallback` — the caller's
 * own, localized words — rather than reach the screen verbatim.
 */
export function extractErrorMessage(err: unknown, fallback: string): string {
  // nginx returns 413 as HTML with no JSON body — map it to a user-facing message.
  const status = (err as { response?: { status?: number } })?.response?.status
  if (status === 413) return 'Image too large — please use a smaller image or a URL instead'

  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const envelope = detail as { code?: unknown; detail?: unknown; errors?: unknown[] }
    if (typeof envelope.code === 'string') {
      // Prefer nested errors array (specific) over wrapper detail string (generic).
      const errors = envelope.errors
      if (Array.isArray(errors) && errors.length > 0) {
        const first = errors[0] as { msg?: string; ctx?: { error?: unknown } }
        const ctxError = first?.ctx?.error
        if (typeof ctxError === 'string' && ctxError) return ctxError
        const msg = first?.msg
        if (typeof msg === 'string') return msg.replace(/^Value error,\s*/i, '')
      }
      if (typeof envelope.detail === 'string') return envelope.detail
    }
  }
  return fallback
}

export default api
